"use client";

import Box from "@mui/material/Box";

import { elevation } from "../tokens/elevation";
import { motion } from "../tokens/motion";
import { radius } from "../tokens/radius";

import type { SxProps, Theme } from "@mui/material/styles";
import type { ElementType, ReactNode } from "react";

export type SurfaceCardProps = {
  children: ReactNode;
  /** Rend la surface entière cliquable. */
  href?: string;
  /**
   * Composant de lien à utiliser avec `href`. Défaut `'a'`.
   *
   * Le design system ne connaît pas le routeur de l'app : une app localisée
   * passe son `Link` i18n, une autre celui de `next/link`. Même contrat que
   * `Pagination`.
   */
  LinkComponent?: ElementType;
  /**
   * `false` retire la bordure — et donc son éclaircissement au survol.
   *
   * Utile quand la carte est déjà entourée : un cadre teinté par le statut
   * ferait apparaître, au survol, un second trait juste à côté du sien.
   */
  bordered?: boolean;
  /** Réservé au rembourrage et à la mise en page interne. */
  sx?: SxProps<Theme>;
  "data-testid"?: string;
  /**
   * Nomme le lien d'après un titre de la carte. Sans lui, son nom accessible
   * concatène tout son texte. Lu seulement avec `href`.
   */
  "aria-labelledby"?: string;
};

/**
 * Surface blanche qui se soulève au survol : la carte de base du socle.
 *
 * Elle remplace quatre copies de la même recette — `DmcCard`,
 * `DestinationCard`, `CategoryCard` et `ProjectCard` — qui déclaraient chacune
 * la bordure transparente, la courbe de transition et l'ombre de survol.
 *
 * Trois arbitrages y sont tranchés :
 *
 * - **le rayon est `lg`**, sans échappatoire. Deux des quatre copies étaient en
 *   `md` ; `lg` est celui des cards Produit et Itinéraire, donc de la majorité
 *   des cards. Une prop `radius` aurait reconduit la divergence qu'on vient de
 *   supprimer ;
 * - **l'ombre de survol vient de `elevation.cardHover`** et non plus de
 *   `2px 2px 10px 0px rgba(0,0,0,0.06)`. Le décalage horizontal de cette
 *   recette était accidentel : elle projetait l'ombre vers la droite, ce qu'une
 *   carte qui se soulève ne fait pas ;
 * - **le mouvement vient de `motion.lift`**, le token de soulèvement du socle.
 *
 * Il n'y a pas de prop `hoverable` : une surface qui ne se soulève pas au
 * survol n'est pas ce composant.
 */
export function SurfaceCard({
  children,
  href,
  LinkComponent = "a",
  bordered = true,
  sx,
  "data-testid": dataTestId,
  "aria-labelledby": ariaLabelledBy,
}: SurfaceCardProps) {
  const surface: SxProps<Theme> = {
    display: "block",
    bgcolor: "background.paper",
    borderRadius: radius.lg,
    overflow: "hidden",
    color: "inherit",
    textDecoration: "none",
    boxShadow: "none",
    ...(bordered
      ? {
          border: "1px solid transparent",
          transition: `box-shadow ${motion.lift}, border-color ${motion.lift}`,
          "&:hover": {
            borderColor: "divider",
            boxShadow: elevation.cardHover,
          },
        }
      : {
          transition: `box-shadow ${motion.lift}`,
          "&:hover": { boxShadow: elevation.cardHover },
        }),
  };

  if (href === undefined) {
    return (
      <Box
        data-testid={dataTestId}
        sx={[surface, ...(Array.isArray(sx) ? sx : [sx])]}
      >
        {children}
      </Box>
    );
  }

  return (
    <Box
      component={LinkComponent}
      href={href}
      aria-labelledby={ariaLabelledBy}
      data-testid={dataTestId}
      sx={[surface, ...(Array.isArray(sx) ? sx : [sx])]}
    >
      {children}
    </Box>
  );
}
